import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSearch, FiX, FiUsers, FiUserCheck, FiUser } from "react-icons/fi";
import { MdVerified } from "react-icons/md";
import messagingService from "../../services/messagingService";
import messagingCacheService from "../../services/messagingCacheService";
import { toast } from "react-toastify";

const NewMessageModal = ({ isOpen, onClose, onSelectContact }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredContacts, setFilteredContacts] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter(
        (contact) =>
          contact.name?.toLowerCase().includes(query) ||
          contact.email?.toLowerCase().includes(query) ||
          contact.researchField?.toLowerCase().includes(query)
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchQuery, contacts]);

  const fetchContacts = async () => {
    // Try cache first for instant display
    const cachedContacts = messagingCacheService.getContacts();
    if (cachedContacts && cachedContacts.contacts) {
      setContacts(cachedContacts.contacts);
      setFilteredContacts(cachedContacts.contacts);
// Don't show loading if we have cached data
    } else {
      setLoading(true);
    }

    try {
      const response = await messagingService.getAvailableContacts({
        page: 1,
        limit: 100,
      });

      if (response.success) {
        const freshContacts = response.data.contacts || [];
        setContacts(freshContacts);
        setFilteredContacts(
          searchQuery.trim()
            ? freshContacts.filter(
                (c) =>
                  c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  c.email?.toLowerCase().includes(searchQuery.toLowerCase())
              )
            : freshContacts
        );

        // Save to persistent cache
        messagingCacheService.setContacts({
          contacts: freshContacts,
          fetchedAt: Date.now(),
        });
      }
    } catch (error) {
// Only show error if we don't have cached data
      if (!cachedContacts) {
        toast.error("Failed to load contacts");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = async (contact) => {
    try {
      const response = await messagingService.startConversation(contact._id);

      if (response.success) {
        onSelectContact(response.data.conversation);
        onClose();
        toast.success(`Started conversation with ${contact.name}`);
      }
    } catch (error) {
toast.error(error.message || "Failed to start conversation");
    }
  };

  const getRelationshipIcon = (contact) => {
    if (contact.relationship?.isMutualFollow) {
      return (
        <FiUserCheck
          className="msgx-modal-relationship-icon mutual"
          title="Mutual follow"
        />
      );
    }
    if (contact.relationship?.hasCollaborated) {
      return (
        <FiUsers
          className="msgx-modal-relationship-icon collaborator"
          title="Collaborator"
        />
      );
    }
    return null;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.charAt(0).toUpperCase();
  };

  const getAvatarGradient = (name) => {
    const gradients = [
      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
    ];
    const index = (name?.charCodeAt(0) || 0) % gradients.length;
    return gradients[index];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="msgx-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="msgx-modal"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="msgx-modal-header">
              <h2 className="msgx-modal-title">New Message</h2>
              <button className="msgx-modal-close" onClick={onClose}>
                <FiX />
              </button>
            </div>

            {/* Search */}
            <div className="msgx-modal-search">
              <FiSearch className="msgx-modal-search-icon" />
              <input
                type="text"
                className="msgx-modal-search-input"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            {/* Body */}
            <div className="msgx-modal-body">
              {loading ? (
                <div className="msgx-modal-loading">
                  <div className="msgx-spinner" />
                  <p>Loading contacts...</p>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="msgx-modal-empty">
                  <div className="msgx-modal-empty-icon">
                    <FiUser />
                  </div>
                  <p className="msgx-modal-empty-text">
                    {searchQuery
                      ? "No contacts match your search"
                      : "You can only send messages to people you follow or have collaborated with"}
                  </p>
                </div>
              ) : (
                <div className="msgx-modal-contacts-list">
                  {filteredContacts.map((contact, index) => (
                    <motion.div
                      key={contact._id}
                      className="msgx-modal-contact-item"
                      onClick={() => handleSelectContact(contact)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ backgroundColor: "var(--msgx-hover)" }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="msgx-modal-contact-avatar">
                        {contact.profilePhotoUrl ? (
                          <img
                            src={contact.profilePhotoUrl}
                            alt={contact.name || "User"}
                          />
                        ) : (
                          <div
                            className="msgx-modal-avatar-initials"
                            style={{
                              background: getAvatarGradient(contact.name),
                            }}
                          >
                            {getInitials(contact.name)}
                          </div>
                        )}
                        <span
                          className={`msgx-modal-status ${
                            contact.isOnline ? "online" : ""
                          }`}
                        />
                      </div>
                      <div className="msgx-modal-contact-info">
                        <div className="msgx-modal-contact-name">
                          {contact.name || "Unknown User"}
                          {contact.isVerified && (
                            <MdVerified
                              className="msgx-modal-verified"
                              title="Verified"
                            />
                          )}
                          {getRelationshipIcon(contact)}
                        </div>
                        <div className="msgx-modal-contact-details">
                          {contact.field && (
                            <span className="msgx-modal-field">
                              {contact.field}
                            </span>
                          )}
                          {contact.professionalRole && (
                            <span className="msgx-modal-role">
                              {contact.professionalRole}
                            </span>
                          )}
                          {contact.relationship?.hasCollaborated && (
                            <span className="msgx-modal-badge">
                              Collaborator
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewMessageModal;
